<script setup>
// Last gate before a send that cannot be recalled. Restates every choice — agent, template,
// segment, recipient count — because by this point the operator has been scrolling for a
// while and the cost of a wrong template is a whole segment receiving it.
//
// The confirmation is an explicit opt-in, not just an OK button: the operator has to tick a
// sentence claiming certainty. A dialog whose primary button is one stray Enter away from
// firing at a few thousand people is not a confirmation, it is a formality.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/ui/Dialog.vue';
import DialogHeader from 'dashboard/components-next/ui/DialogHeader.vue';
import DialogTitle from 'dashboard/components-next/ui/DialogTitle.vue';
import Button from 'dashboard/components-next/ui/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  open: { type: Boolean, default: false },
  agentName: { type: String, default: '' },
  templateName: { type: String, default: '' },
  previewImageUrl: { type: String, default: '' },
  segmentName: { type: String, default: '' },
  senderPhone: { type: String, default: '' },
  count: { type: Number, default: 0 },
  // Quantos desta lista pediram para parar: o servidor os pula em qualquer disparo (REPORTS-03, G7).
  optedOutCount: { type: Number, default: 0 },
  // Quantas linhas da lista eles ocupam: o mesmo telefone pode vir em dois contatos. Sem isto, uma por pessoa.
  optedOutRows: { type: Number, default: null },
  sending: { type: Boolean, default: false },
});

const emit = defineEmits(['update:open', 'confirm']);

const { t } = useI18n();

const acknowledged = ref(false);

// Quem recebe de fato: a lista menos as linhas de quem pediu para parar. É este o número que
// Destinatários e o botão repetem. O tamanho da lista diria que ela inteira recebeu.
const recipients = computed(() =>
  Math.max(props.count - (props.optedOutRows ?? props.optedOutCount), 0)
);
// Todos pediram para parar: não sobra ninguém, e o botão não pode prometer disparo nenhum.
const allOptedOut = computed(() => props.count > 0 && recipients.value === 0);

// Every open starts unchecked. Carrying the tick over from a previous campaign would hand
// the next one a pre-confirmed dialog.
watch(
  () => props.open,
  isOpen => {
    if (isOpen) acknowledged.value = false;
  }
);
</script>

<template>
  <Dialog :open="open" @update:open="emit('update:open', $event)">
    <DialogHeader>
      <DialogTitle>{{ t('OUTBOUNDS.DISPATCH.CONFIRM_TITLE') }}</DialogTitle>
    </DialogHeader>

    <img
      v-if="previewImageUrl"
      :src="previewImageUrl"
      :alt="templateName"
      class="object-cover w-full mt-4 rounded-lg max-h-40 bg-n-alpha-2"
    />

    <dl class="mt-4 space-y-2 text-sm">
      <div class="flex justify-between gap-4">
        <dt class="text-n-slate-10">{{ t('OUTBOUNDS.DISPATCH.AGENT') }}</dt>
        <dd class="font-medium text-right text-n-slate-12">{{ agentName }}</dd>
      </div>
      <div v-if="senderPhone" class="flex justify-between gap-4">
        <dt class="text-n-slate-10">{{ t('OUTBOUNDS.DISPATCH.SENDER') }}</dt>
        <dd class="text-right tabular-nums text-n-slate-12">
          {{ senderPhone }}
        </dd>
      </div>
      <div class="flex justify-between gap-4">
        <dt class="text-n-slate-10">{{ t('OUTBOUNDS.DISPATCH.TEMPLATE') }}</dt>
        <dd class="font-medium text-right break-all text-n-slate-12">
          {{ templateName }}
        </dd>
      </div>
      <div class="flex justify-between gap-4">
        <dt class="text-n-slate-10">{{ t('OUTBOUNDS.DISPATCH.SEGMENT') }}</dt>
        <dd class="text-right text-n-slate-12">{{ segmentName }}</dd>
      </div>
      <div class="flex justify-between gap-4 pt-2 border-t border-n-weak">
        <dt class="text-n-slate-10">
          {{ t('OUTBOUNDS.DISPATCH.RECIPIENTS') }}
        </dt>
        <dd
          data-test="recipients"
          class="text-lg font-semibold tabular-nums text-n-slate-12"
        >
          {{
            recipients === count
              ? recipients
              : t('OUTBOUNDS.DISPATCH.RECIPIENTS_OF', {
                  count: recipients,
                  total: count,
                })
          }}
        </dd>
      </div>
    </dl>

    <div
      v-if="optedOutCount"
      data-test="opted-out"
      class="flex items-start gap-2 px-3 py-2 mt-4 text-xs border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
    >
      <i class="i-lucide-circle-slash size-4 mt-0.5 shrink-0 text-n-ruby-11" />
      <p>
        <span v-if="allOptedOut" class="font-medium">
          {{ t('OUTBOUNDS.DISPATCH.ALL_OPTED_OUT') }}
        </span>
        <span v-else class="font-medium">
          {{
            t(
              'OUTBOUNDS.DISPATCH.OPTED_OUT',
              { count: optedOutCount },
              optedOutCount
            )
          }}
        </span>
        {{ t('OUTBOUNDS.DISPATCH.OPTED_OUT_SCOPE') }}
      </p>
    </div>

    <p class="mt-4 px-3 py-2 text-xs rounded-lg bg-n-amber-2 text-n-amber-11">
      {{ t('OUTBOUNDS.DISPATCH.IRREVERSIBLE') }}
    </p>

    <label
      class="flex items-start gap-2 px-3 py-3 mt-3 text-sm border rounded-lg cursor-pointer select-none border-n-weak bg-n-alpha-1 text-n-slate-12"
    >
      <Checkbox
        v-model="acknowledged"
        :disabled="sending"
        class="mt-0.5 shrink-0"
      />
      <span>{{ t('OUTBOUNDS.DISPATCH.ACKNOWLEDGE') }}</span>
    </label>

    <div class="flex justify-end gap-2 mt-5">
      <Button
        variant="ghost"
        :disabled="sending"
        @click="emit('update:open', false)"
      >
        {{ t('OUTBOUNDS.CANCEL') }}
      </Button>
      <Button
        :disabled="sending || !recipients || !acknowledged"
        @click="emit('confirm')"
      >
        <i v-if="sending" class="mr-1 i-lucide-loader-2 size-4 animate-spin" />
        {{ t('OUTBOUNDS.DISPATCH.CONFIRM', { count: recipients }) }}
      </Button>
    </div>
  </Dialog>
</template>
